import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchGroupsAsync } from '../store/slices/groupsSlice';
import {
  fetchAbsencesByDay,
  fetchStudentLessons,
  fetchPercentByDay,
  setFilters as setAnalyticsFilters,
  clearStudentLessons,
} from '../store/slices/analyticsSlice';
import './Students.css';
import './GroupForm.css';
import { ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, LineChart, Line, BarChart, Bar } from 'recharts';
import { fetchAbsencesByGroup, fetchTopStudents } from '../store/slices/analyticsSlice';

const formatDate = (d: Date) => d.toISOString().slice(0, 10);

const Absences: React.FC = () => {
  const dispatch = useAppDispatch();
  const { groups } = useAppSelector((s) => s.groups);
  const analytics = useAppSelector((s) => s.analytics);
  const loading = analytics.loading;

  // Default last 30 days
  const today = useMemo(() => new Date(), []);
  const defaultTo = useMemo(() => formatDate(today), [today]);
  const defaultFrom = useMemo(() => formatDate(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)), [today]);

  const [dateFrom, setDateFrom] = useState<string>(defaultFrom);
  const [dateTo, setDateTo] = useState<string>(defaultTo);
  const [level, setLevel] = useState<string>('');
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [topNValue, setTopNValue] = useState<string>('5');
  const [topNError, setTopNError] = useState<string | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<number | undefined>(undefined);
  const [chartType, setChartType] = useState<'percent_day' | 'day' | 'group' | 'student'>('percent_day');

  useEffect(() => {
    // Load groups for multi-select
    dispatch(fetchGroupsAsync({ page: 1, limit: 200 }));
  }, [dispatch]);

  // Dispatch the data fetch for the given chart. Does not touch local
  // component state, so it is safe to call directly from effects.
  const runFilters = useCallback((chart: typeof chartType, topNParsed: number | undefined) => {
    const filters = {
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
      level: level || undefined,
      top: topNParsed,
      studentId: selectedStudentId,
    };
    dispatch(setAnalyticsFilters(filters));
    // Fetch only the selected chart's data
    if (chart === 'percent_day') {
      dispatch(fetchPercentByDay(filters));
      dispatch(clearStudentLessons());
    } else if (chart === 'day') {
      dispatch(fetchAbsencesByDay(filters));
      dispatch(clearStudentLessons());
    } else if (chart === 'group') {
      dispatch(fetchAbsencesByGroup(filters));
      dispatch(clearStudentLessons());
    } else if (chart === 'student') {
      dispatch(fetchTopStudents(filters));
      dispatch(clearStudentLessons());
    }
  }, [dispatch, dateFrom, dateTo, selectedGroupIds, level, selectedStudentId]);

  // Validate the Top-students input (only relevant for the student chart) and
  // dispatch the fetch for the given chart type.
  const applyFiltersFor = useCallback((chart: typeof chartType) => {
    let topNParsed: number | undefined = undefined;
    if (chart === 'student') {
      if (topNValue.trim() === '') {
        setTopNError('Enter a number between 1 and 50');
        return;
      }
      const n = parseInt(topNValue, 10);
      if (isNaN(n) || n < 1 || n > 50) {
        setTopNError('Enter a number between 1 and 50');
        return;
      }
      topNParsed = n;
      setTopNError(null);
    }
    runFilters(chart, topNParsed);
  }, [runFilters, topNValue]);

  const applyFilters = useCallback(() => {
    applyFiltersFor(chartType);
  }, [applyFiltersFor, chartType]);

  useEffect(() => {
    // initial load with defaults (last 30 days) for the default chart type.
    // Default chart is not 'student', so no Top-students validation runs here.
    runFilters('percent_day', undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Multi-select dropdown (checkbox style)
  const [groupsOpen, setGroupsOpen] = useState(false);
  const toggleGroup = (id: number) => {
    setSelectedGroupIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const clearGroups = () => setSelectedGroupIds([]);

  // Global keydown to submit on Enter for filter sections
  const onFiltersKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applyFilters();
    }
  };

  const handleStudentClick = (studentId: number) => {
    setSelectedStudentId(studentId);
    const filters = {
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
      level: level || undefined,
      studentId,
    };
    dispatch(setAnalyticsFilters(filters));
    dispatch(fetchStudentLessons(filters));
  };

  // No pivot helper needed for group aggregated bar chart

  // For Top Students chart (bar), we use analytics.topStudents directly

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Absences Analytics</h1>
          <p>Analyze absences by day, by groups, and by person</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar" onKeyDown={onFiltersKeyDown}>
        <label htmlFor="date-from" className="filter-label">From</label>
        <input id="date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="form-input" />

        <label htmlFor="date-to" className="filter-label">To</label>
        <input id="date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="form-input" />

        <label htmlFor="level" className="filter-label">Level</label>
        <select id="level" value={level} onChange={(e) => setLevel(e.target.value)} className="form-select">
          <option value="">All</option>
          <option value="a1">A1</option>
          <option value="a2">A2</option>
          <option value="b1">B1</option>
          <option value="b2">B2</option>
          <option value="c1">C1</option>
          <option value="c2">C2</option>
        </select>

        <span className="filter-label">Groups</span>
        <div style={{ position: 'relative' }}>
          <button type="button" className="form-select" onClick={() => setGroupsOpen(o => !o)} style={{ textAlign: 'left' }}>
            {selectedGroupIds.length === 0 ? 'All groups' : `${selectedGroupIds.length} selected`}
          </button>
          {groupsOpen && (
            <div style={{ position: 'absolute', zIndex: 10, background: '#fff', border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: 8, width: '100%', maxHeight: 220, overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <button type="button" className="btn-secondary" onClick={clearGroups}>Clear</button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => { applyFilters(); setGroupsOpen(false); }}
                >
                  Done
                </button>
              </div>
              {groups.map(g => (
                <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedGroupIds.includes(g.id)}
                    onChange={() => toggleGroup(g.id)}
                  />
                  <span>{g.name || `${g.level.toUpperCase()} Group (${g.id})`}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {chartType === 'student' && (
          <>
            <label htmlFor="topN" className="filter-label">Top students</label>
            <input
              id="topN"
              type="number"
              min={1}
              max={50}
              value={topNValue}
              onChange={(e) => setTopNValue(e.target.value)}
              className="form-input"
            />
            {topNError && (
              <span className="error-text">{topNError}</span>
            )}
          </>
        )}
        <button className="btn-primary" onClick={applyFilters} disabled={loading}>Apply</button>
      </div>

      {analytics.error && (
        <div className="error-message">{analytics.error}</div>
      )}

      {/* Chart selector */}
      <div className="filter-bar" onKeyDown={onFiltersKeyDown}>
        <label htmlFor="chart-type" className="filter-label">Chart</label>
        <select
          id="chart-type"
          value={chartType}
          onChange={(e) => {
            const v = e.target.value as 'percent_day' | 'day' | 'group' | 'student';
            setChartType(v);
            // Refetch only the newly selected chart (matches prior effect behavior)
            applyFiltersFor(v);
          }}
          className="form-select"
        >
          <option value="percent_day">% Absent by Day (default)</option>
          <option value="day">Absences by Day</option>
          <option value="group">Absences by Group</option>
          <option value="student">Absences by Student</option>
        </select>
      </div>

      {/* Single chart area */}
      <div className="students-table-container" style={{ marginTop: 8 }}>
        {chartType === 'percent_day' && (
          <>
            <h3 style={{ marginLeft: 20 }}>% Absent by Day</h3>
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <LineChart data={analytics.percentByDay} margin={{ top: 10, right: 20, left: 20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} />
                  <Tooltip formatter={(value: number | string, name: number | string) => name === 'percent' ? [`${value}%`, 'Percent'] : [value, name]} />
                  <Line dataKey="percent" type="monotone" stroke="#3366ff" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {chartType === 'day' && (
          <>
            <h3 style={{ marginLeft: 20 }}>Absences by Day</h3>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={analytics.byDay} margin={{ top: 10, right: 20, left: 20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line dataKey="absences" type="monotone" stroke="#8884d8" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {chartType === 'group' && (
          <>
            <h3 style={{ marginLeft: 20 }}>Absences by Group</h3>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <BarChart
                  data={analytics.byGroup.map(g => ({ name: g.groupName || `Group ${g.groupId}`, absences: g.absences }))}
                  layout="vertical"
                  margin={{ top: 10, right: 20, left: 20, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={220} />
                  <Tooltip />
                  <Bar dataKey="absences" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {chartType === 'student' && (
          <>
            <h3 style={{ marginLeft: 20 }}>Absences by Student</h3>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={analytics.topStudents.map(s => ({ ...s, name: `${s.firstName} ${s.lastName}` }))} layout="vertical" margin={{ top: 10, right: 20, left: 20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={200} />
                  <Tooltip />
                  <Bar dataKey="absences" fill="#ffc658" onClick={(data: { payload: { studentId: number } }) => handleStudentClick(data.payload.studentId)} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>

      {/* Detail: selected student's absences by lessons */}
      {chartType === 'student' && analytics.studentLessons && analytics.studentLessons.length > 0 && (
        <div className="students-table-container" style={{ marginTop: 24 }}>
          <h3>Absences by Lessons for selected student</h3>
          <table className="students-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Group</th>
                <th>Level</th>
              </tr>
            </thead>
            <tbody>
              {analytics.studentLessons.map((row) => (
                <tr key={row.lessonId}>
                  <td>{row.date}</td>
                  <td>{row.groupName || `Group ${row.groupId}`}</td>
                  <td>{row.level}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading && (
        <div className="loading-overlay"><div className="loading-spinner" /></div>
      )}
    </div>
  );
};

export default Absences;
